import React, { useState, useEffect, useCallback } from 'react';
import moment from 'moment';
import { wallClock } from './utils/wallClock';
import Select from 'react-select';
import { XMarkIcon } from '@heroicons/react/24/outline';
import ConfirmModal from './components/ConfirmModal';
import ErrorModal from './components/ErrorModal';
import RecommendedTasksPanel from './components/RecommendedTasksPanel';
import { API } from './api/api';
import { canChangeProject } from './utils/events';
import { isProjectBudgetLocked, BUDGET_LOCKED_MESSAGE } from './utils/projectBudget';
import { hasMineFlags, visibleProjects } from './utils/projectFilter';

// Helper functions for time formatting
const formatDecimalHoursToHHMM = (decimalHours) => {
  const hours = Math.floor(decimalHours);
  const minutes = Math.round((decimalHours - hours) * 60);
  return `${hours}:${minutes.toString().padStart(2, '0')}`;
};

const parseHHMMToDecimalHours = (timeString) => {
  const [hours, minutes] = timeString.split(':').map(Number);
  return hours + (minutes / 60);
};

// Parse flexible time input - supports both decimal (0,1) and HH:MM (1:30) formats
const parseFlexibleTimeToDecimalHours = (timeString) => {
  if (!timeString || !timeString.trim()) return 0;
  
  const trimmed = timeString.trim();
  
  // Check if it's decimal format (contains comma)
  if (trimmed.includes(',')) {
    const decimalValue = parseFloat(trimmed.replace(',', '.'));
    if (isNaN(decimalValue)) throw new Error('Invalid decimal format');
    return decimalValue;
  }
  
  // Check if it's HH:MM format (contains colon)
  if (trimmed.includes(':')) {
    return parseHHMMToDecimalHours(trimmed);
  }
  
  // If it's just a number, treat as decimal hours
  const decimalValue = parseFloat(trimmed);
  if (isNaN(decimalValue)) throw new Error('Invalid number format');
  return decimalValue;
};

export default function EventFormModal({ isOpen, onClose, onSave, start, end, projects, initialData, onDelete, selectedSlotDate, currentView, modalPosition, onTimeChange, setModalPosition, getModalPositionStyle, onAddPeopleEvent, onUpdatePeopleEvent, onNavigate, currentDate, defaultUseNotes, canUseBD }) {
  const [date, setDate] = useState('');
  const [startTime, setStartTime] = useState('');
  const [hours, setHours] = useState('');
  const [projectId, setProjectId] = useState('');
  const [showBudgetLocked, setShowBudgetLocked] = useState(false);
  const [onlyMyProjects, setOnlyMyProjects] = useState(true);
  const [timecodeId, setTimecodeId] = useState('');
  const [title, setTitle] = useState('');
  const [endTime, setEndTime] = useState('');
  const [lastChangedField, setLastChangedField] = useState(null);
  const [errors, setErrors] = useState({});
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [sendToPeople, setSendToPeople] = useState(false);
  const isEditingPeopleEvent = initialData?.isPeople;

  const [entryType, setEntryType] = useState('Udvikling');
  const [akut, setAkut] = useState(false); // <-- NEW
  const [projectTasks, setProjectTasks] = useState([]);
  const [selectedTaskId, setSelectedTaskId] = useState('');
  const [isLoadingTasks, setIsLoadingTasks] = useState(false);
  const [useNotesInsteadOfTask, setUseNotesInsteadOfTask] = useState(defaultUseNotes ?? false);
  const [taskDone, setTaskDone] = useState(false);
  const [createTask, setCreateTask] = useState(false);
  const [isEnhancingNotes, setIsEnhancingNotes] = useState(false);

  const [showRecommendedTasks, setShowRecommendedTasks] = useState(false);

  // Tracks which entry we've already initialized form fields for, to prevent
  // onTimeChange-triggered re-renders from resetting user-typed notes/fields.
  const initializedLogIdRef = React.useRef(null);

  // State for tracking screen size for responsive modal positioning
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  // Auto-open recommended tasks panel when modal opens (new entries only)
  useEffect(() => {
    if (isOpen && !initialData && !isMobile) {
      setShowRecommendedTasks(true);
    }
  }, [isOpen, initialData, isMobile]);

  const hideProjectAndNotes = sendToPeople || isEditingPeopleEvent;

  const showSendToPeopleToggle = !initialData || isEditingPeopleEvent;

  // Prevent body scroll when modal is open on mobile
  useEffect(() => {
    if (isOpen && isMobile) {
      document.body.classList.add('modal-open');
      return () => {
        document.body.classList.remove('modal-open');
      };
    }
  }, [isOpen, isMobile]);

  const readonly = initialData?.approval_status === 'Approved';
  const canEditProject = !readonly && canChangeProject(initialData);

  // Handler when a recommended task is picked from the panel
  const handleSelectRecommendedTask = useCallback((task) => {
    if (isProjectBudgetLocked(projects.find(p => p.id === task.projectId))) {
      setShowBudgetLocked(true);
      return;
    }
    // Set the project (this triggers the useEffect that loads tasks for the project)
    setProjectId(task.projectId);
    // Set the task name as the title
    setTitle(task.name);
    // Pre-select the task ID so it auto-matches once project tasks load
    setSelectedTaskId(task.id);
    // Ensure we're in task mode, not notes mode
    setUseNotesInsteadOfTask(false);
    // Close the panel
    setShowRecommendedTasks(false);
  }, [projects]);

  const resetForm = () => {
    setDate('');
    setStartTime('');
    setHours('');
    setProjectId('');
    setTimecodeId('');
    setTitle('');
    setEndTime('');
    setEntryType('Udvikling');
    setAkut(false); // <-- NEW
    setTaskDone(false);
    setCreateTask(false);
    setProjectTasks([]);
    setSelectedTaskId('');
    setUseNotesInsteadOfTask(false);
    setShowRecommendedTasks(false);
    initializedLogIdRef.current = null;
  };

  useEffect(() => {
    if (!isOpen) return;
    if (initialData && !isEditingPeopleEvent) {
      setSendToPeople(false);
    }
  }, [isOpen, initialData, isEditingPeopleEvent]);

  useEffect(() => {
    if (hasSubmitted && Object.keys(errors).length === 0) {
      setHasSubmitted(false);
    }
  }, [errors, hasSubmitted]);

  // Listen for window resize to update mobile state for responsive modal positioning
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Update startTime/endTime -> hours
    useEffect(() => {
      if (lastChangedField === 'startTime' || lastChangedField === 'endTime') {
        if (startTime && endTime && date) {
          const startMoment = moment(`${date} ${startTime}`, 'YYYY-MM-DD HH:mm');
          let endMoment = moment(`${date} ${endTime}`, 'YYYY-MM-DD HH:mm');
          
          // If end time is before or equal to start time, assume it's the next day
          if (endMoment.isSameOrBefore(startMoment)) {
            endMoment = endMoment.add(1, 'day');
          }
          
          const diffInHours = endMoment.diff(startMoment, 'minutes') / 60;
          setHours(formatDecimalHoursToHHMM(diffInHours));

          // Notify parent to update event in calendar
          if (onTimeChange) {
            onTimeChange({
              ...(initialData || {}),
              start: startMoment.toDate(),
              end: endMoment.toDate(),
              logId: initialData?.logId || null,
            });
          }
        }
      }
    }, [startTime, endTime, date]); 

  useEffect(() => {
    if (initialData) {
      // Always sync time/date fields so the calendar reflects time drag changes.
      setDate(moment(initialData.start).format('YYYY-MM-DD'));
      setStartTime(moment(initialData.start).format('HH:mm'));
      const durationMs = moment(initialData.end).diff(moment(initialData.start));
      const durationHours = durationMs / 3600000;
      setHours(formatDecimalHoursToHHMM(durationHours));
      setEndTime(moment(initialData.end).format('HH:mm'));

      // Only initialize user-editable fields when opening a different entry.
      // Without this guard, onTimeChange causes the parent to update start/end,
      // which re-fires this effect and overwrites notes the user just typed.
      const logId = initialData.logId;
      if (logId !== initializedLogIdRef.current) {
        initializedLogIdRef.current = logId;
        setProjectId(initialData.projectId || '');
        setTimecodeId(initialData.timecodeId || '');
        setTitle(initialData.title || '');
        setEntryType(initialData.type || '');
        setAkut(!!initialData.akut);
        setSelectedTaskId(initialData.selectedTaskId || '');
        if (!initialData.selectedTaskId && initialData.title) {
          setUseNotesInsteadOfTask(true);
        }
      }
    } else if (start && end) {
      setDate(moment(start).format('YYYY-MM-DD'));
      setStartTime(moment(start).format('HH:mm'));
      setEndTime(moment(end).format('HH:mm'));
      const durationMs = moment(end).diff(moment(start));
      const durationHours = durationMs / 3600000; // ms to hours
      setHours(formatDecimalHoursToHHMM(durationHours));
    }
  }, [start, end, initialData]);
  
  useEffect(() => {
    if (!isOpen) return;

    if (readonly) return;

    const handleArrowKeys = (event) => {
      const tag = document.activeElement.tagName;
      const isEditable = (
        tag === 'INPUT' ||
        tag === 'TEXTAREA' ||
        tag === 'SELECT' ||
        document.activeElement.isContentEditable
      );
      if (isEditable) return;

      if (!readonly && (event.key === 'ArrowUp' || event.key === 'ArrowDown')) {
        event.preventDefault();

        const startMoment = moment(`${date} ${startTime}`, 'YYYY-MM-DD HH:mm');
        const endMoment = moment(`${date} ${endTime}`, 'YYYY-MM-DD HH:mm');
        if (!startMoment.isValid() || !endMoment.isValid()) return;

        const diff = endMoment.diff(startMoment, 'minutes');
        if (diff <= 0) return;

        const delta = event.key === 'ArrowUp' ? -15 : 15;

        if (event.shiftKey) {
          // Shift + Arrow: move only end time
          const newEnd = endMoment.clone().add(delta, 'minutes');
          // Check if new duration would exceed 23 hours
          const newDurationHours = newEnd.diff(startMoment, 'minutes') / 60;
          // Prevent end before start, after day end, or duration over 23 hours
          if (
            newEnd.isSameOrBefore(startMoment) ||
            newEnd.isAfter(moment(date).endOf('day').add(1, 'minute')) ||
            newDurationHours > 23
          ) {
            return;
          }
          setEndTime(newEnd.format('HH:mm'));
          setLastChangedField('endTime');

          // Notify parent
          if (onTimeChange && initialData) {
            onTimeChange({
              ...initialData,
              start: startMoment.toDate(),
              end: newEnd.toDate(),
            });
          }
          if (onTimeChange && !initialData && start && end) {
            onTimeChange({
              start: startMoment.toDate(),
              end: newEnd.toDate(),
              logId: null,
            });
          }
        } else {
          // Arrow: move both start and end
          const newStart = startMoment.clone().add(delta, 'minutes');
          const newEnd = endMoment.clone().add(delta, 'minutes');
          // Check if duration would exceed 23 hours (should remain same, but double-check)
          const durationHours = newEnd.diff(newStart, 'minutes') / 60;
          if (
            newStart.isBefore(moment(date).startOf('day')) ||
            newEnd.isAfter(moment(date).endOf('day').add(1, 'minute')) ||
            durationHours > 23
          ) {
            return;
          }
          setStartTime(newStart.format('HH:mm'));
          setEndTime(newEnd.format('HH:mm'));
          setLastChangedField('startTime');

          // Notify parent
          if (onTimeChange && initialData) {
            onTimeChange({
              ...initialData,
              start: newStart.toDate(),
              end: newEnd.toDate(),
            });
          }
          if (onTimeChange && !initialData && start && end) {
            onTimeChange({
              start: newStart.toDate(),
              end: newEnd.toDate(),
              logId: null,
            });
          }
        }
      }
      if ((event.key === 'ArrowLeft' || event.key === 'ArrowRight') && (currentView === 'week' || currentView === 'work_week' || currentView === 'three_day')) {
        event.preventDefault();

        const startMoment = moment(`${date} ${startTime}`, 'YYYY-MM-DD HH:mm');
        const endMoment = moment(`${date} ${endTime}`, 'YYYY-MM-DD HH:mm');
        if (!startMoment.isValid() || !endMoment.isValid()) return;

        const dayOfWeek = startMoment.day(); // 0=Sunday, 1=Monday, ..., 6=Saturday

        // Week view: block left on Monday, right on Sunday
        if (currentView === 'week') {
          if (event.key === 'ArrowLeft' && dayOfWeek === 1) return;
          if (event.key === 'ArrowRight' && dayOfWeek === 0) return;
        }
        // Work week: block left on Monday, right on Friday
        if (currentView === 'work_week') {
          if (event.key === 'ArrowLeft' && dayOfWeek === 1) return;
          if (event.key === 'ArrowRight' && dayOfWeek === 5) return;
          // Also block moving to weekend
          if (event.key === 'ArrowRight' && dayOfWeek >= 5) return;
          if (event.key === 'ArrowLeft' && dayOfWeek <= 1) return;
        }

        // Calculate new start/end
        const deltaDays = event.key === 'ArrowLeft' ? -1 : 1;
        const newStart = startMoment.clone().add(deltaDays, 'days');
        const newEnd = endMoment.clone().add(deltaDays, 'days');

        // For work_week, prevent moving to Sat/Sun
        if (currentView === 'work_week') {
          const newDay = newStart.day();
          if (newDay === 0 || newDay === 6) return;
        }


        setDate(newStart.format('YYYY-MM-DD'));
        setStartTime(newStart.format('HH:mm'));
        setEndTime(newEnd.format('HH:mm'));
        setLastChangedField('startTime');

        // For three_day view, check if we need to navigate the calendar
        let shouldUpdateModalPosition = true;
        if (currentView === 'three_day' && onNavigate && currentDate) {
          // Calculate current 3-day range shown in calendar
          const calendarStart = moment(currentDate).startOf('day');
          const calendarEnd = moment(calendarStart).add(2, 'days').endOf('day');
          
          // Check if new date is outside current 3-day range
          if (newStart.isBefore(calendarStart, 'day') || newStart.isAfter(calendarEnd, 'day')) {
            // Navigate calendar to show the new date
            onNavigate(newStart.toDate());
            // Delay modal position update with the new calculated range
            shouldUpdateModalPosition = false;
            setTimeout(() => {
              // Calculate the new 3-day range that will be shown
              const newRangeStart = newStart.startOf('day');
              const dayIndex = newStart.diff(newRangeStart, 'days'); // This will be 0
              
              // Position modal based on the new range
              let newPositionStyle = { right: '20px' };
              if (dayIndex === 2) {
                newPositionStyle = { left: '20px' };
              }
              
              setModalPosition({
                ...newPositionStyle,
                position: 'fixed',
                top: '80px',
                zIndex: 2600,
              });
            }, 50);
          }
        }

        // Update modal position so it doesn't cover the event (for views without navigation)
        if (shouldUpdateModalPosition) {
          setModalPosition(getModalPositionStyle(newStart.toDate(), currentView));
        }

        // Notify parent
        if (onTimeChange && initialData) {
          onTimeChange({
            ...initialData,
            start: newStart.toDate(),
            end: newEnd.toDate(),
          });
        }
        if (onTimeChange && !initialData && start && end) {
          onTimeChange({
            start: newStart.toDate(),
            end: newEnd.toDate(),
            logId: null,
          });
        }
      }
    };

    document.addEventListener('keydown', handleArrowKeys);
    return () => document.removeEventListener('keydown', handleArrowKeys);
  }, [isOpen, date, startTime, endTime, readonly, onTimeChange, initialData, start, end]);

  useEffect(() => {
    if (projectId) {
      setIsLoadingTasks(true);
      API.fetchTasksByProject(projectId)
        .then((data) => {
          if (data.success && data.data && Array.isArray(data.data)) {
            console.log('✅ Fetched project tasks:', data.data);
            setProjectTasks(data.data);
          } else {
            console.log('❌ No project tasks in response:', data);
            setProjectTasks([]);
          }
          setIsLoadingTasks(false);
        })
        .catch((err) => {
          console.error('❌ Error fetching project tasks:', err);
          setProjectTasks([]);
          setIsLoadingTasks(false);
        });
    } else {
      setProjectTasks([]);
      setSelectedTaskId('');
    }
  }, [projectId, projects]);

  // Auto-match selectedTaskId from title when project tasks are loaded
  useEffect(() => {
    if (projectTasks.length > 0 && title && !selectedTaskId) {
      const matchingTask = projectTasks.find(task => task.name === title);
      if (matchingTask) {
        console.log('✅ Auto-matched task from title:', matchingTask);
        setSelectedTaskId(matchingTask.id);
      }
    }
  }, [projectTasks, title, selectedTaskId]);

  useEffect(() => {
    validateForm();
  }, [date, startTime, hours, projectId, timecodeId, title]);

  // Tilføj event listener for Escape-tasten
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape' && isOpen) {
        handleClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, hasChanges, readonly]);

  const validateForm = () => {
    const newErrors = {};

    if (!date) newErrors.date = "Please select a date";
    if (!startTime) newErrors.startTime = "Please enter a start time";
    if (!hours) newErrors.hours = "Please enter hours";
    else {
      // Support both HH:MM and decimal formats
      const hhmmPattern = /^\d{1,2}:\d{2}$/;
      const decimalPattern = /^\d*[,.]?\d+$/;
      
      if (!hhmmPattern.test(hours) && !decimalPattern.test(hours)) {
        newErrors.hours = "Use format like 1:30, 0,1 or 1.5";
      } else {
        try {
          const numericHours = parseFlexibleTimeToDecimalHours(hours);
          if (numericHours <= 0) newErrors.hours = "Hours must be greater than zero";
          if (numericHours > 23) newErrors.hours = "Hours must be less than 23";
          if (!(sendToPeople || isEditingPeopleEvent) && numericHours > 3 && entryType !== 'Workshop') {
            newErrors.hours = "A single time log cannot exceed 3 hours";
          }
        } catch (e) {
          newErrors.hours = "Invalid time format";
        }
      }
    }

    // Only require projectId and title if not sending to People AND not editing a Zoho People event
    if (!(sendToPeople || isEditingPeopleEvent)) {
      if (!projectId) newErrors.projectId = "Please select a project";

      // If using notes instead of task, validate notes; otherwise validate task selection
      if (useNotesInsteadOfTask) {
        if (!title.trim()) newErrors.title = "Please enter notes";
        else if (title.trim().length < 25) newErrors.title = "Notes must be at least 25 characters";
      } else {
        if (!selectedTaskId) {
          newErrors.selectedTaskId = "Please select a task";
        } else {
          const selectedTask = projectTasks.find(t => t.id === selectedTaskId);
          if (selectedTask && selectedTask.name.length < 25) {
            newErrors.selectedTaskId = "Task name is too short — please switch to notes and add more details";
          }
        }
      }
    }

    if (!(sendToPeople || isEditingPeopleEvent) && !entryType) {
      newErrors.type = "Please select a type";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    setHasSubmitted(true);
    const isValid = validateForm();
    if (!isValid) {
      console.log('Form validation failed:', errors);
      return;
    }

    const startDate = moment(`${date} ${startTime}`, 'YYYY-MM-DD HH:mm').toDate();
    let floatHours;
    try {
      floatHours = parseFlexibleTimeToDecimalHours(hours);
    } catch (e) {
      setErrors(prev => ({ ...prev, hours: 'Invalid time format' }));
      return;
    }
    if (isNaN(floatHours) || floatHours <= 0) {
      setErrors(prev => ({ ...prev, hours: 'Invalid hours entered' }));
      return;
    }

    if (isEditingPeopleEvent) {
      API.updatePeopleTimelog({
        timeLogId: (initialData.timelogId || initialData.logId || '').toString().replace(/^people:/, ''),
        workDate: date,
        jobId: initialData.jobId || timecodeId,
        hours: floatHours,
        fromTime: startTime,
        toTime: endTime,
        billingStatus: initialData.billingStatus || 'billable',
        description: title,
        workItem: '',
      })
        .then((data) => {
          if (data.success && data.data) {
            if (onUpdatePeopleEvent) onUpdatePeopleEvent(data.data);
            resetForm();
            onClose();
          } else {
            setErrors(prev => ({ ...prev, api: 'Failed to update Zoho People event' }));
          }
        })
        .catch((err) => {
          console.error('Zoho People update error:', err);
          setErrors(prev => ({ ...prev, api: 'Failed to update Zoho People event' }));
        });
      return;
    }

    if (sendToPeople && !initialData) {
      API.createPeopleTimelog({
        start: wallClock(startDate),
        end: wallClock(new Date(startDate.getTime() + floatHours * 3600000)),
        jobId: timecodeId,
        workDate: date,
        hours: floatHours,
        workItem: '',
        billingStatus: 'billable',
        description: '',
      })
        .then((data) => {
          if (data.success && data.data) {
            if (onAddPeopleEvent) onAddPeopleEvent(data.data);
            resetForm();
            onClose();
          } else {
            setErrors(prev => ({ ...prev, api: 'Failed to send to Zoho People' }));
          }
        })
        .catch((err) => {
          console.error('Zoho People create error:', err);
          setErrors(prev => ({ ...prev, api: 'Failed to send to Zoho People' }));
        });
      return;
    }

    const durationMs = floatHours * 3600000; // Convert hours to ms
    const endDate = new Date(startDate.getTime() + durationMs);

    // Log overnight event detection for debugging
    if (endDate.getDate() !== startDate.getDate()) {
      console.log('📅 Overnight event detected:', {
        start: startDate.toISOString(),
        end: endDate.toISOString(),
        hours: floatHours
      });
    }

    // Use selected task name as title if a task is selected and not in notes mode
    let finalTitle = title;
    if (!useNotesInsteadOfTask && selectedTaskId) {
      const selectedTask = projectTasks.find(task => task.id === selectedTaskId);
      if (selectedTask) {
        finalTitle = selectedTask.name;
      }
    }

    onSave({
      title: finalTitle,
      start: startDate,
      end: endDate,
      projectId,
      type: entryType,
      akut,
      ...(taskDone && (selectedTaskId || createTask) ? { taskDone: true } : {}),
      ...(useNotesInsteadOfTask && createTask ? { createTask: true } : {}),
      billingtype: 'Billable',
      logId: initialData?.logId || null,
      projectName: projectOptions.find(p => p.value === projectId)?.label || '',
      timecodeName: '',
      selectedTaskId: selectedTaskId || '',
    });
    setHasSubmitted(false);
    resetForm();
    onClose();
  };


  const handleDelete = () => {
    setShowDeleteConfirmModal(true);
  };

  const handleConfirmDelete = () => {
    // Pass isPeople so caller uses correct API and strips prefix
    onDelete(initialData?.logId, initialData?.projectId, initialData?.timecodeId, initialData?.isPeople);
    resetForm();
    onClose();
    setShowDeleteConfirmModal(false);
  };

  const handleCancelDelete = () => {
    setShowDeleteConfirmModal(false);
  };

  const handleEnhanceNotes = async () => {
    if (isEnhancingNotes) return;
    const notesInput = useNotesInsteadOfTask
      ? title
      : projectTasks.find(t => t.id === selectedTaskId)?.name || '';
    if (!notesInput.trim()) return;
    setIsEnhancingNotes(true);
    try {
      const projectName = projects.find(p => p.id === projectId)?.name || '';
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/enhance-notes`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ notes: notesInput, projectName, entryType }),
      });
      const data = await res.json();
      if (data.success) {
        setTitle(data.enhanced.substring(0, 255));
        setSelectedTaskId('');
        setUseNotesInsteadOfTask(true);
      }
    } catch (e) {
      // silently fail
    } finally {
      setIsEnhancingNotes(false);
    }
  };

  const handleClose = () => {
    if (hasChanges && !readonly) {
      setShowConfirmModal(true);
    } else {
      resetForm();
      onClose();
      setHasSubmitted(false);
      setHasChanges(false);
    }
  };

  const handleConfirmClose = () => {
    setShowConfirmModal(false);
    resetForm();
    onClose();
    setHasSubmitted(false);
    setHasChanges(false);
  };

  const handleCancelClose = () => {
    setShowConfirmModal(false);
  };

  // Tjek for ændringer når inputs ændres
  useEffect(() => {
    if (!readonly) {
      setHasChanges(
        projectId !== (initialData?.projectId || '') ||
        timecodeId !== (initialData?.timecodeId || '') ||
        title !== (initialData?.title || '') ||
        akut !== false || // <-- NEW
        selectedTaskId !== (initialData?.selectedTaskId || '')
      );
    }
  }, [projectId, timecodeId, title, initialData, readonly, akut, selectedTaskId]);

  if (!isOpen) return null;

  const canToggleProjects = hasMineFlags(projects);
  const projectOptions = visibleProjects(projects, !onlyMyProjects, projectId).map((p) => ({
    value: p.id,
    label: p.name,
  }));

  // "Business Development" is only offered to users with the Projektleder
  // role (see admin panel) — everyone else never sees it as a pickable
  // option. Exception: if the entry being edited already has that type
  // (set before the user lost/never had access), keep showing it so the
  // dropdown doesn't render blank and an unrelated edit doesn't silently
  // change the type.
  const canShowBD = canUseBD || entryType === 'Business Development';
  const typeOptions = [
    { value: 'Udvikling', label: 'Udvikling' },
    { value: 'Møde', label: 'Møde' },
    { value: 'Support', label: 'Support' },
    ...(canShowBD ? [{ value: 'Business Development', label: 'Business Development' }] : []),
    { value: 'Workshop', label: 'Workshop' },
  ];

  // Mobile-first positioning: center on mobile with respect to fixed header/footer
  const mobileStyles = {
    position: 'fixed',
    top: '20px', // Minimal margin from top (header will be 70px + 20px = 90px from top)
    left: '50%',
    transform: 'translateX(-50%)', // Only center horizontally
    zIndex: 2600,
    bottom: '20px', // Minimal margin from bottom (footer will be 90px + 20px = 110px from bottom)
    // Let the modal content scroll if needed
    display: 'flex',
    flexDirection: 'column',
  };

  const finalModalPosition = isMobile ? mobileStyles : modalPosition;

  return (
    <>
      {/* Backdrop overlay for all screen sizes */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 md:bg-transparent"
        style={{ zIndex: 2500 }}
        onClick={handleClose}
      />
        
      <div
        className="bg-[#0D1A27] text-white rounded-lg w-full max-w-[88vw] md:w-[400px] h-auto max-h-full md:max-h-[90vh] border border-[#273C61] modal-mobile flex flex-col"
        style={finalModalPosition}
        onClick={(e) => e.stopPropagation()}
      >
          {/* Modal Header */}
          <div className="flex items-center justify-between mb-3 p-3 md:p-6 pb-0 md:pb-0">
            <h2 className="text-xl font-semibold text-white flex items-center gap-2">
              {initialData ? 'Edit' : 'New entry'}
              {!initialData && (
                <a
                  href="https://timelogguide.zrm.dk"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs font-normal text-blue-500 hover:text-blue-700 underline"
                  onClick={(e) => e.stopPropagation()}
                >
                  ZRM - Timelog guidelines
                </a>
              )}
            </h2>
            <button
              onClick={handleClose}
              className="text-white/50 hover:text-white p-1 transition-colors"
              aria-label="Close"
            >
              <XMarkIcon className="w-6 h-6" />
            </button>
          </div>

          {/* Scrollable Content */}
          <div className="flex-1 overflow-y-auto p-3 md:p-6 pt-0 md:pt-0">

          {/* Approved Warning Badge */}
          {readonly && (
            <div className="mb-3 p-3 bg-[#0D1A27] border border-[#FF7800] rounded-md">
              <p className="text-white text-sm font-bold">
                If you wish to change this approved registration, please{' '}
                <a 
                  href={(() => {
                    const zuid = new URLSearchParams(window.location.search).get('zuid');
                    const formatDate = (dateStr) => {
                      const date = new Date(dateStr);
                      return date.toLocaleDateString('en-GB', {
                        day: '2-digit',
                        month: '2-digit', 
                        year: 'numeric'
                      });
                    };
                    // Strip people:/projects: prefix for display
                    const rawLogId = (initialData?.logId || '').toString();
                    const cleanLogId = rawLogId.replace(/^(people|projects):/, '');

                    const subject = `Timelog ${cleanLogId} - ${initialData?.start ? formatDate(initialData.start) : ''}`;
                    const body = `Hello - regarding timelog ${cleanLogId} on ${initialData?.start ? formatDate(initialData.start) : ''} for ${initialData?.projectName || ''} with ${initialData?.timecodeName || ''} I have the following request:

XX

zuid: ${zuid || ''}`;
                    
                    return `mailto:finans@zrm.dk?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
                  })()}
                  className="underline hover:text-yellow-200"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  contact Finance
                </a>.
              </p>
            </div>
          )}

          {/* Date og Hours - side by side on mobile like Start Time/End Time */}
          <div className="mb-2 grid grid-cols-2 md:flex md:flex-row gap-2 md:gap-4">
            <div className="flex flex-col md:w-1/2">
              <label className="text-sm md:text-base font-medium mb-1">Date:</label>
              <input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className={`p-2 text-base rounded border border-[#273C61] w-full touch-manipulation text-white bg-[#111e2e] ${readonly ? 'opacity-50' : ''}`}
                disabled={readonly}
              />
              {hasSubmitted && errors.date && <div className="text-red-500 text-sm mt-1">{errors.date}</div>}
            </div>
            <div className="flex flex-col md:w-1/2">
              <label className="text-sm md:text-base font-medium mb-1">Hours:</label>
              <input
                type="text"
                value={hours}
                onChange={(e) => {
                  // Allow numbers, colon, comma, and period for flexible input
                  let val = e.target.value.replace(/[^0-9:,.]/g, '');
                  setHours(val);
                  setLastChangedField('hours');
                }}
                onBlur={() => {
                  // Try to parse and validate the hours input using flexible parser
                  try {
                    const parsed = parseFlexibleTimeToDecimalHours(hours);
                    if (!isNaN(parsed) && parsed > 0 && parsed <= 23) {
                      // Valid: update end time
                      const startMoment = moment(`${date} ${startTime}`, 'YYYY-MM-DD HH:mm');
                      const endMoment = startMoment.clone().add(parsed, 'hours');
                      setEndTime(endMoment.format('HH:mm'));
                      // Notify parent
                      if (onTimeChange) {
                        onTimeChange({
                          ...(initialData || {}),
                          start: startMoment.toDate(),
                          end: endMoment.toDate(),
                          logId: initialData?.logId || null,
                        });
                      }
                    } else {
                      // Invalid: revert to current diff between start and end
                      const startMoment = moment(`${date} ${startTime}`, 'YYYY-MM-DD HH:mm');
                      const endMoment = moment(`${date} ${endTime}`, 'YYYY-MM-DD HH:mm');
                      if (startMoment.isValid() && endMoment.isValid() && endMoment.isAfter(startMoment)) {
                        const diffInHours = endMoment.diff(startMoment, 'minutes') / 60;
                        setHours(formatDecimalHoursToHHMM(diffInHours));
                      } else {
                        setHours('');
                      }
                    }
                  } catch (e) {
                    // Invalid format: revert to current diff between start and end
                    const startMoment = moment(`${date} ${startTime}`, 'YYYY-MM-DD HH:mm');
                    const endMoment = moment(`${date} ${endTime}`, 'YYYY-MM-DD HH:mm');
                    if (startMoment.isValid() && endMoment.isValid() && endMoment.isAfter(startMoment)) {
                      const diffInHours = endMoment.diff(startMoment, 'minutes') / 60;
                      setHours(formatDecimalHoursToHHMM(diffInHours));
                    } else {
                      setHours('');
                    }
                  }
                }}
                className={`p-2 text-base rounded border border-[#273C61] w-full touch-manipulation text-white bg-[#111e2e] ${readonly ? 'opacity-50' : ''}`}
                disabled={readonly}
                placeholder="E.g. 1:30 or 0,1 or 1.5"
              />
              {hasSubmitted && errors.hours && <div className="text-red-500 text-sm mt-1">{errors.hours}</div>}
            </div>
          </div>

          {/* Start Time og End Time - side by side on mobile like SeriesModal */}
          <div className="mb-2 grid grid-cols-2 md:flex md:flex-row gap-2 md:gap-4">
            <div className="flex flex-col md:w-1/2">
              <label className="text-sm md:text-base font-medium mb-1">Start Time:</label>
              <input
                type="time"
                value={startTime}
                onChange={(e) => {
                  setStartTime(e.target.value);
                  setLastChangedField('startTime');
                }}
                className={`p-2 text-base rounded border border-[#273C61] w-full touch-manipulation text-white bg-[#111e2e] ${readonly ? 'opacity-50' : ''}`}
                disabled={readonly}
              />
              {hasSubmitted && errors.startTime && <div className="text-red-500 text-sm mt-1">{errors.startTime}</div>}
            </div>
            <div className="flex flex-col md:w-1/2">
              <label className="text-sm md:text-base font-medium mb-1">End Time:</label>
              <input
                type="time"
                value={endTime}
                onChange={(e) => {
                  setEndTime(e.target.value);
                  setLastChangedField('endTime');
                }}
                className={`p-2 text-base rounded border border-[#273C61] w-full touch-manipulation text-white bg-[#111e2e] ${readonly ? 'opacity-50' : ''}`}
                disabled={readonly}
              />
              {hasSubmitted && errors.endTime && <div className="text-red-500 text-sm mt-1">{errors.endTime}</div>}
            </div>
          </div>

          {/* Project som fuld bredde */}
          {!hideProjectAndNotes && (
          <div className="mb-2 flex flex-col">
            <div className="mb-1 flex items-center gap-2">
              <label className="text-sm md:text-base font-medium">
                Project:
              </label>
              {canToggleProjects && (
                <input
                  type="checkbox"
                  aria-label="My projects"
                  title="My projects"
                  className="h-4 w-4 accent-[#FF7800] cursor-pointer opacity-60 hover:opacity-100 transition-opacity"
                  checked={onlyMyProjects}
                  onChange={(e) => setOnlyMyProjects(e.target.checked)}
                />
              )}
            </div>
            <Select
              options={projectOptions}
              value={projectOptions.find(option => option.value === projectId)}
              onChange={(selectedOption) => {
                if (!initialData || canEditProject) {
                  if (isProjectBudgetLocked(projects.find(p => p.id === selectedOption?.value))) {
                    setProjectId('');
                    setShowBudgetLocked(true);
                    return;
                  }
                  setProjectId(selectedOption?.value || '');
                }
              }}
              isClearable={!initialData || canEditProject}
              isDisabled={!!initialData && !canEditProject}
              placeholder="Search or select a project..."
              menuPlacement="auto"
              menuPortalTarget={document.body}
              styles={{
                menuPortal: (provided) => ({ ...provided, zIndex: 9999 }),
                control: (provided, state) => ({
                  ...provided,
                  minHeight: '44px',
                  padding: '2px 4px',
                  fontSize: '16px',
                  backgroundColor: state.isDisabled ? '#0a1520' : '#111e2e',
                  borderColor: '#273C61',
                  '&:hover': { borderColor: '#205799' },
                }),
                placeholder: (provided) => ({ ...provided, fontSize: '16px', color: 'rgba(255,255,255,0.3)' }),
                input: (provided) => ({ ...provided, fontSize: '16px', color: 'white' }),
                singleValue: (provided) => ({ ...provided, fontSize: '16px', color: 'white' }),
                menu: (provided) => ({ ...provided, backgroundColor: '#111e2e', border: '1px solid #273C61' }),
                menuList: (provided) => ({ ...provided, backgroundColor: '#111e2e' }),
                option: (provided, state) => ({
                  ...provided,
                  backgroundColor: state.isFocused ? '#273C61' : state.isSelected ? '#205799' : '#111e2e',
                  color: 'white',
                  fontSize: '16px',
                }),
                indicatorSeparator: (provided) => ({ ...provided, backgroundColor: '#273C61' }),
                dropdownIndicator: (provided) => ({ ...provided, color: 'rgba(255,255,255,0.5)' }),
                clearIndicator: (provided) => ({ ...provided, color: 'rgba(255,255,255,0.5)' }),
              }}
            />
            {hasSubmitted && errors.projectId && <div className="text-red-500 text-sm mt-1">{errors.projectId}</div>}
          </div>
          )}

          {!hideProjectAndNotes && (
            <div className="mb-2 flex flex-col">
              <label className="text-sm md:text-base font-medium mb-1">Type:</label>
              <Select
                options={typeOptions}
                value={typeOptions.find(o => o.value === entryType) || null}
                onChange={(opt) => setEntryType(opt?.value || '')}
                isClearable
                isDisabled={readonly}
                placeholder="Select type..."
                menuPlacement="auto"
                menuPortalTarget={document.body}
                styles={{
                  control: (provided, state) => ({
                    ...provided,
                    minHeight: '44px',
                    padding: '2px 4px',
                    fontSize: '16px',
                    backgroundColor: state.isDisabled ? '#0a1520' : '#111e2e',
                    borderColor: '#273C61',
                    '&:hover': { borderColor: '#205799' },
                  }),
                  placeholder: (provided) => ({ ...provided, fontSize: '16px', color: 'rgba(255,255,255,0.3)' }),
                  input: (provided) => ({ ...provided, fontSize: '16px', color: 'white' }),
                  singleValue: (provided) => ({ ...provided, fontSize: '16px', color: 'white' }),
                  menuPortal: (provided) => ({ ...provided, zIndex: 9999 }),
                  menu: (provided) => ({ ...provided, backgroundColor: '#111e2e', border: '1px solid #273C61', zIndex: 9999 }),
                  menuList: (provided) => ({ ...provided, backgroundColor: '#111e2e', maxHeight: '200px' }),
                  option: (provided, state) => ({
                    ...provided,
                    backgroundColor: state.isFocused ? '#273C61' : state.isSelected ? '#205799' : '#111e2e',
                    color: 'white',
                    fontSize: '16px',
                  }),
                  indicatorSeparator: (provided) => ({ ...provided, backgroundColor: '#273C61' }),
                  dropdownIndicator: (provided) => ({ ...provided, color: 'rgba(255,255,255,0.5)' }),
                  clearIndicator: (provided) => ({ ...provided, color: 'rgba(255,255,255,0.5)' }),
                }}
              />
              {hasSubmitted && errors.type && (
                <div className="text-red-500 text-sm mt-1">{errors.type}</div>
              )}
            </div>
          )}

          {/* Notes or Task Selection */}
          {!hideProjectAndNotes && projectId && (() => {
              // Show toggle button and either task dropdown or notes based on user choice
              const taskOptions = projectTasks.map((task) => ({
                value: task.id,
                label: task.name,
              }));

              return (
                <>
                  <div className="mb-2 flex flex-col">
                    <div className="flex items-center justify-between mb-1">
                      <label className="text-sm md:text-base font-medium">
                        {useNotesInsteadOfTask ? 'Notes:' : 'Task:'}
                      </label>
                      <div className="flex items-center gap-2">
                        {!readonly && (useNotesInsteadOfTask || selectedTaskId) && (
                          <button
                            type="button"
                            onClick={handleEnhanceNotes}
                            disabled={isEnhancingNotes || (useNotesInsteadOfTask ? !title.trim() : !selectedTaskId)}
                            className="text-xs md:text-sm text-[#FF7800] hover:text-[#FF9933] disabled:opacity-40"
                            title="Enhance notes with AI"
                          >
                            {isEnhancingNotes ? '...' : '✨ Enhance'}
                          </button>
                        )}
                        {!readonly && (
                          <button
                            type="button"
                            onClick={() => {
                              setUseNotesInsteadOfTask(!useNotesInsteadOfTask);
                              // Clear the other field when switching
                              if (useNotesInsteadOfTask) {
                                setTitle('');
                                setCreateTask(false);
                                setTaskDone(false);
                              } else {
                                const selectedTask = projectTasks.find(t => t.id === selectedTaskId);
                                if (selectedTask) setTitle(selectedTask.name);
                                setSelectedTaskId('');
                              }
                            }}
                            className="text-xs md:text-sm text-[#FF7800] hover:text-[#FF9933] underline"
                          >
                            Switch to {useNotesInsteadOfTask ? 'Task' : 'Notes'}
                          </button>
                        )}
                      </div>
                    </div>
                    
                    {useNotesInsteadOfTask ? (
                      // Show notes textarea
                      <>
                        <textarea
                          value={title}
                          onChange={(e) => setTitle(e.target.value.trimStart())}
                          className={`p-2 text-base rounded border border-[#273C61] resize-none touch-manipulation text-white bg-[#111e2e] ${readonly ? 'opacity-50' : ''}`}
                          placeholder="Enter notes..."
                          disabled={readonly}
                          maxLength={255}
                          rows={4}
                          style={{ fontSize: '16px' }}
                        />
                        <div className="text-sm text-white/50 text-right">
                          {title.length}/255
                        </div>
                        {hasSubmitted && errors.title && <div className="text-red-500 text-sm mt-1">{errors.title}</div>}
                        {!readonly && !sendToPeople && !isEditingPeopleEvent && (
                          <div className="mt-2 flex items-center">
                            <input
                              type="checkbox"
                              id="createTask"
                              className="mr-3 h-5 w-5 md:h-4 md:w-4 accent-[#FF7800] cursor-pointer touch-manipulation"
                              checked={createTask}
                              onChange={(e) => {
                                setCreateTask(e.target.checked);
                                if (!e.target.checked) setTaskDone(false);
                              }}
                            />
                            <label htmlFor="createTask" className="cursor-pointer text-sm md:text-base">Create task</label>
                          </div>
                        )}
                      </>
                    ) : (
                      // Show task dropdown
                      <>
                        <Select
                          options={taskOptions}
                          value={taskOptions.find(option => option.value === selectedTaskId)}
                          onChange={(selectedOption) => {
                            const task = projectTasks.find(t => t.id === selectedOption?.value);
                            if (task && task.name.length < 25) {
                              // Switch to notes mode and pre-fill with task name
                              setUseNotesInsteadOfTask(true);
                              setTitle(task.name);
                              setSelectedTaskId('');
                            } else {
                              setSelectedTaskId(selectedOption?.value || '');
                            }
                          }}
                          isClearable
                          isDisabled={readonly || isLoadingTasks}
                          placeholder={isLoadingTasks ? 'Loading tasks...' : 'Search or select a task...'}
                          menuPlacement="auto"
                          menuPortalTarget={document.body}
                          styles={{
                            control: (provided, state) => ({
                              ...provided,
                              minHeight: '44px',
                              padding: '2px 4px',
                              fontSize: '16px',
                              backgroundColor: state.isDisabled ? '#0a1520' : '#111e2e',
                              borderColor: '#273C61',
                              cursor: state.isDisabled ? 'not-allowed' : 'pointer',
                              '&:hover': { borderColor: '#205799' },
                            }),
                            placeholder: (provided) => ({ ...provided, fontSize: '16px', color: 'rgba(255,255,255,0.3)' }),
                            input: (provided) => ({ ...provided, fontSize: '16px', color: 'white' }),
                            singleValue: (provided) => ({ ...provided, fontSize: '16px', color: 'white' }),
                            menuPortal: (provided) => ({ ...provided, zIndex: 9999 }),
                            menu: (provided) => ({ ...provided, backgroundColor: '#111e2e', border: '1px solid #273C61', zIndex: 9999 }),
                            menuList: (provided) => ({ ...provided, backgroundColor: '#111e2e', maxHeight: '200px' }),
                            option: (provided, state) => ({
                              ...provided,
                              backgroundColor: state.isFocused ? '#273C61' : state.isSelected ? '#205799' : '#111e2e',
                              color: 'white',
                              fontSize: '16px',
                            }),
                            indicatorSeparator: (provided) => ({ ...provided, backgroundColor: '#273C61' }),
                            dropdownIndicator: (provided) => ({ ...provided, color: 'rgba(255,255,255,0.5)' }),
                            clearIndicator: (provided) => ({ ...provided, color: 'rgba(255,255,255,0.5)' }),
                          }}
                        />
                        {hasSubmitted && errors.selectedTaskId && (
                          <div className="text-red-500 text-sm mt-1">{errors.selectedTaskId}</div>
                        )}
                      </>
                    )}
                  </div>
                </>
              );
          })()}

          {showSendToPeopleToggle && (
            <div className="mb-2 flex items-center">
              <input
                type="checkbox"
                id="sendToPeople"
                className="mr-3 h-5 w-5 md:h-4 md:w-4 accent-[#FF7800] cursor-pointer touch-manipulation"
                checked={isEditingPeopleEvent ? true : sendToPeople}
                onChange={isEditingPeopleEvent ? undefined : (e) => setSendToPeople(e.target.checked)}
                disabled={isEditingPeopleEvent}
                readOnly={isEditingPeopleEvent}
              />
              <label htmlFor="sendToPeople" className="cursor-pointer text-sm md:text-base">
                Send to People
              </label>
            </div>
          )}


          {initialData?.draft && (
            <div className="mb-3 text-sm text-[#FF7800]">
              Kladde: bliver først en rigtig tidsregistrering, når du gemmer.
            </div>
          )}

          {/* AKUT checkbox */}
          {!sendToPeople && !isEditingPeopleEvent && (
            <div className="mb-3 flex items-center">
              <input
                type="checkbox"
                id="akut"
                className="mr-3 h-5 w-5 md:h-4 md:w-4 accent-[#FF7800] cursor-pointer touch-manipulation"
                disabled={readonly}
                checked={akut}
                onChange={(e) => setAkut(e.target.checked)}
              />
              <label htmlFor="akut" className="cursor-pointer text-sm md:text-base">AKUT</label>
            </div>
          )}

          {/* Task Done checkbox - when a task is selected, or when in notes mode with createTask checked */}
          {!sendToPeople && !isEditingPeopleEvent && ((!useNotesInsteadOfTask && selectedTaskId) || (useNotesInsteadOfTask && createTask)) && (
            <div className="mb-3 flex items-center">
              <input
                type="checkbox"
                id="taskDone"
                className="mr-3 h-5 w-5 md:h-4 md:w-4 accent-[#FF7800] cursor-pointer touch-manipulation"
                disabled={readonly}
                checked={taskDone}
                onChange={(e) => setTaskDone(e.target.checked)}
              />
              <label htmlFor="taskDone" className="cursor-pointer text-sm md:text-base">Task done</label>
            </div>
          )}

          </div> {/* End Scrollable Content */}

          {/* Sticky Action Buttons */}
          <div className="border-t border-[#273C61] p-3 md:p-6 bg-[#0D1A27] rounded-b-lg">
          <div className="flex flex-col md:flex-row justify-between gap-3 md:gap-0">
            {!readonly && (
              <>
                <div className="order-2 md:order-1">
                  {initialData && (
                    <button
                      onClick={handleDelete}
                      className="w-full md:w-auto px-6 py-3 md:px-4 md:py-2 bg-red-600 text-white rounded hover:bg-red-700 touch-manipulation font-medium"
                    >
                      Delete
                    </button>
                  )}
                </div>
                <div className="order-1 md:order-2">
                  <button
                    onClick={handleSubmit}
                    className="w-full md:w-auto px-6 py-3 md:px-4 md:py-2 bg-[#FF7800] text-white rounded hover:bg-[#e06800] disabled:opacity-50 touch-manipulation font-medium"
                    disabled={readonly}
                  >
                    Save
                  </button>
                </div>
              </>
            )}
          </div>
          </div> {/* End Sticky Action Buttons */}
        </div>
      
      {/* Recommended Tasks Side Panel */}
      <RecommendedTasksPanel
        isOpen={showRecommendedTasks}
        onClose={() => setShowRecommendedTasks(false)}
        onSelectTask={handleSelectRecommendedTask}
        isMobile={isMobile}
        modalPosition={modalPosition}
      />

      <ConfirmModal 
        isOpen={showConfirmModal}
        onConfirm={handleConfirmClose}
        onCancel={handleCancelClose}
        title="Unsaved Changes"
        message="Are you sure you want to discard your changes?"
        confirmText="Discard"
        confirmButtonClass="bg-red-600 hover:bg-red-700"
      />
      <ConfirmModal 
        isOpen={showDeleteConfirmModal}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
        title="Delete Registration"
        message="Are you sure you want to delete this time registration?"
        confirmText="Delete"
        confirmButtonClass="bg-red-600 hover:bg-red-700"
      />
      <ErrorModal
        isOpen={showBudgetLocked}
        onClose={() => setShowBudgetLocked(false)}
        title="Budget opbrugt"
        message={BUDGET_LOCKED_MESSAGE}
      />
    </>
  );
}